import React from 'react';

import { LinkOutlined } from '@ant-design/icons';
import { Modal } from 'antd';
import { useFormik } from 'formik';

import { MODAL_WIDTH } from './AddWebhookModal';
import { useUpsertWebhookConfig } from '../api/upsertWebhookConfig';
import { useUpsertWebhookCredentials } from '../api/upsertWebhookCredentials';
import { buildWebhookFormSchema } from '../schemas/webhookFormSchema';
import { IWebhookConfig } from '../types';
import { IWebhookFormValues, WebhookForm } from './WebhookForm';
import { stripIrrelevantAuthFields } from '../webhookFormHelpers';
import CancelSubmitFooterModal from '@/components/CancelSubmitFooterModal';
import useToasts from '@/shared/hooks/useToasts';
import { MessageType } from '@/shared/hooks/useToasts.types';

interface IEditWebhookModalProps {
  partnerId: string;
  config: IWebhookConfig;
  open: boolean;
  onClose: () => void;
}

export const EditWebhookModal = ({
  partnerId,
  config,
  open,
  onClose,
}: IEditWebhookModalProps) => {
  const addToast = useToasts((state) => state.addItem);

  const { mutateAsync: upsertWebhookConfig, isPending: isPendingConfig } =
    useUpsertWebhookConfig({ partnerId });

  const {
    mutateAsync: upsertWebhookCredentials,
    isPending: isPendingCredentials,
  } = useUpsertWebhookCredentials({ partnerId });

  const isPending = isPendingConfig || isPendingCredentials;

  const validationSchema = buildWebhookFormSchema({
    originalAuthType: config.auth_type,
  });

  const formik = useFormik<IWebhookFormValues>({
    initialValues: {
      url: config.url,
      auth_type: config.auth_type,
      basic_username: '',
      basic_password: '',
      header_name: '',
      header_value: '',
      query_parameter: '',
      query_value: '',
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        const { credentials, config: configValues } =
          stripIrrelevantAuthFields(values);
        await upsertWebhookConfig({
          configId: config.config_id,
          values: { ...config, ...configValues },
        });
        const hasAuthTypeChanged = values.auth_type !== config.auth_type;
        const hasCredentialValues = Object.entries(credentials)
          .filter(([key]) => key !== 'auth_type')
          .some(([, v]) => typeof v === 'string' && v.length > 0);

        if (hasAuthTypeChanged || hasCredentialValues) {
          await upsertWebhookCredentials({
            configId: config.config_id,
            values: credentials,
          });
        }
        addToast(
          { msg: 'Webhook URL updated', type: MessageType.Success },
          'root',
          {}
        );
        onClose();
      } catch (err) {
        addToast(
          {
            msg: err instanceof Error ? err.message : 'Update failed',
            type: MessageType.Error,
          },
          'root',
          {}
        );
      }
    },
  });

  const handleCancel = () => {
    formik.resetForm();
    onClose();
  };

  return (
    <Modal
      title={
        <span>
          <LinkOutlined /> Edit Webhook
        </span>
      }
      open={open}
      centered
      destroyOnHidden
      footer={CancelSubmitFooterModal(
        !formik.isValid || !formik.dirty,
        formik.handleSubmit,
        handleCancel,
        isPending
      )}
      onCancel={handleCancel}
      width={MODAL_WIDTH}
    >
      <WebhookForm isUpdating formik={formik} />
    </Modal>
  );
};
